import { supabase } from '../lib/supabase';
import { Profile } from '../react/context/AuthContext';

export const adminService = {
  async getDistinctControlPlaces(): Promise<string[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('control_place')
      .eq('role', 'patient')
      .not('control_place', 'is', null);
      
    if (error) throw error;
    
    const unique = new Set<string>();
    data.forEach(d => {
      if (d.control_place) unique.add(d.control_place);
    });
    
    return Array.from(unique);
  },

  async getDoctors(): Promise<(Profile & { assigned_places: string[] })[]> {
    const { data: doctorsData, error: dError } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'doctor');
      
    if (dError) throw dError;
    
    // Also fetch superadmin if they also can be assigned places? 
    // Usually doctors are strictly 'doctor' role but just in case, we fetch 'doctor' only based on req.

    const { data: placesData, error: pError } = await supabase
      .from('doctor_control_places')
      .select('*');
      
    if (pError) throw pError;
    
    return doctorsData.map((doc: any) => {
      return {
        ...doc,
        assigned_places: placesData
          .filter(p => p.doctor_id === doc.id)
          .map(p => p.control_place)
      };
    });
  },

  async assignDoctorToControlPlace(doctorId: string, controlPlace: string): Promise<void> {
    const { error } = await supabase
      .from('doctor_control_places')
      .insert({ doctor_id: doctorId, control_place: controlPlace });
      
    if (error) throw error;
  },

  async removeDoctorFromControlPlace(doctorId: string, controlPlace: string): Promise<void> {
    const { error } = await supabase
      .from('doctor_control_places')
      .delete()
      .match({ doctor_id: doctorId, control_place: controlPlace });
      
    if (error) throw error;
  },

  async getAllPatients(): Promise<Profile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'patient')
      .order('full_name', { ascending: true });
    if (error) throw error;
    return data as Profile[];
  },

  async getPatientsForDoctor(doctorId: string): Promise<Profile[]> {
    const { data: places, error: pError } = await supabase
      .from('doctor_control_places')
      .select('control_place')
      .eq('doctor_id', doctorId);
      
    if (pError) throw pError;
    const allowedPlaces = places.map(p => p.control_place);
    
    if (allowedPlaces.length === 0) return [];

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'patient')
      .in('control_place', allowedPlaces)
      .order('full_name', { ascending: true });
      
    if (error) throw error;
    return data as Profile[];
  },

  async getPatientHistory(patientId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('injection_history')
      .select('*')
      .eq('user_id', patientId)
      .order('date', { ascending: false })
      .order('time', { ascending: false });
      
    if (error) throw error;
    return data;
  },

  async createDoctorUser(email: string, password: string, fullName: string): Promise<void> {
    // Save current session so we can restore it if signup auto-signs in the new user
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        // Disable email redirect so it doesn't interfere
        emailRedirectTo: undefined,
      },
    });

    if (error) throw error;

    const newUserId = data.user?.id;
    if (!newUserId) throw new Error('Gagal mendapatkan ID pengguna baru');

    // Insert the profile with role=doctor
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: newUserId,
        full_name: fullName,
        role: 'doctor',
      });

    if (profileError) throw profileError;

    // If signup auto-signed in as the new user, restore the admin session
    const { data: { session: afterSession } } = await supabase.auth.getSession();
    if (currentSession && afterSession?.user?.id !== currentSession.user.id) {
      await supabase.auth.setSession({
        access_token: currentSession.access_token,
        refresh_token: currentSession.refresh_token,
      });
    }
  }
};

